import { Box, Typography, Stack, IconButton } from '@mui/material';
import { Download, Description } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DocumentInOffer } from '../../search/types/search.types';
import { useParentApp } from '@/shared/hooks/useParentApp';
import offerDetailService from '../services/offerDetailService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const BACKDOOR_TOKEN_KEY = 'backdoor_token';

interface AttachedFilesSectionProps {
  documents: DocumentInOffer[];
}

export default function AttachedFilesSection({ documents }: AttachedFilesSectionProps) {
  const { t } = useTranslation();
  const { isInsideIframe, requestDownload } = useParentApp();

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      if (isInsideIframe) {
        const downloadUrl = `${window.location.origin}${API_BASE_URL}/offers/documents/${documentId}/download`;
        const token =
          sessionStorage.getItem(BACKDOOR_TOKEN_KEY) ||
          localStorage.getItem(BACKDOOR_TOKEN_KEY) ||
          '';
        requestDownload(downloadUrl, filename, token);
      } else {
        const blob = await offerDetailService.downloadDocument(documentId);
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t('offerDetail.attachedFiles')}
      </Typography>
      {!documents || documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('offerDetail.noAttachedFiles')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {documents.map(doc => (
            <Box
              key={doc.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                <Description sx={{ color: 'primary.main' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.filename}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(doc.fileSize)}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleDownload(doc.id, doc.filename)}
                aria-label={t('offerDetail.download', { filename: doc.filename })}
              >
                <Download />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
